import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Seo from "../components/site/Seo";
import PageHero from "../components/site/PageHero";
import Reveal from "../components/site/Reveal";
import { SectionLabel, Chip, PrimaryButton, PlaceholderNote, ArrowLink } from "../components/site/Bits";
import { Field, TextAreaField, SelectField, FormSuccess } from "../components/site/Fields";
import ClosingCTA from "../components/home/ClosingCTA";
import { SEO, CAREERS, COMPANY } from "../lib/content";
import { IMAGES } from "../lib/images";
import { fetchOpenings, postApplication, extractError } from "../lib/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/;

const emptyForm = { name: "", email: "", phone: "", area: "", portfolio: "", opening: "", message: "" };

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Careers() {
  const [openings, setOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchOpenings()
      .then((data) => alive && setOpenings(data))
      .catch(() => alive && setOpenings([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const applyTo = (opening) => {
    setForm((prev) => ({
      ...prev,
      opening: opening.title,
      area: CAREERS.opportunities.includes(opening.department) ? opening.department : prev.area,
    }));
    setSent(false);
    scrollToId("apply");
  };

  const validate = () => {
    const next = {};
    if (form.name.trim().length < 2) next.name = "Please enter your full name.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Please enter a valid email address.";
    if (form.phone.trim().length < 6) next.phone = "Please enter a contact number.";
    if (!form.area) next.area = "Please choose an area of interest.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await postApplication(form);
      setSent(true);
      setForm(emptyForm);
      toast.success("Your profile has reached the LYKIT team.");
    } catch (error) {
      toast.error(extractError(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo title={SEO.careers.title} description={SEO.careers.description} path="/careers" />

      <PageHero
        eyebrow="Careers"
        titleLines={CAREERS.heading}
        intro={CAREERS.intro}
        image={IMAGES.careers.hero}
      >
        <div className="flex flex-wrap gap-3">
          <PrimaryButton variant="ivory" onClick={() => scrollToId("openings")} data-testid="careers-view-openings-cta">
            {CAREERS.ctaPrimary}
          </PrimaryButton>
          <PrimaryButton variant="on-dark" onClick={() => scrollToId("apply")} data-testid="careers-send-profile-cta">
            {CAREERS.ctaSecondary}
          </PrimaryButton>
        </div>
      </PageHero>

      {/* people we look for */}
      <section className="lk-section" data-testid="careers-traits-section">
        <div className="lk-container-wide">
          <div className="grid gap-y-12 lg:grid-cols-12 lg:gap-x-14">
            <div className="lg:col-span-4">
              <Reveal>
                <SectionLabel>{CAREERS.traitsLabel}</SectionLabel>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="display-md mt-7 max-w-[16ch]">THE PEOPLE WE BUILD WITH.</h2>
              </Reveal>
              <Reveal variant="image" delay={0.16} className="mt-12 hidden lg:block">
                <div className="img-frame img-zoom" style={{ aspectRatio: "4 / 3" }}>
                  <img src={IMAGES.careers.culture} alt="LYKIT team at work" loading="lazy" />
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7 lg:col-start-6">
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {CAREERS.traits.map((trait, i) => (
                  <Reveal
                    as="li"
                    key={trait.title}
                    delay={i * 0.05}
                    className="flex flex-col gap-2 py-7 sm:flex-row sm:items-baseline sm:gap-10"
                    style={{ borderTop: "1px solid rgba(23,21,34,0.10)" }}
                  >
                    <span
                      className="shrink-0 font-display text-[12px] font-bold"
                      style={{ letterSpacing: "0.16em", color: "rgba(23,21,34,0.38)", width: 40 }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-display text-[clamp(16px,1.3vw,19px)] font-extrabold tracking-[-0.02em]">
                        {trait.title}
                      </h3>
                      <p className="mt-2 max-w-[44ch] text-[14px] leading-[1.62] body-muted">{trait.text}</p>
                    </div>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* opportunity areas */}
      <section style={{ background: "var(--lykit-lavender)" }} data-testid="careers-areas-section">
        <div className="lk-container-wide py-[clamp(36px,3.8vw,62px)]">
          <Reveal>
            <SectionLabel>Opportunities</SectionLabel>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="display-md mt-7 max-w-[22ch]">WHERE YOU COULD WORK WITH US.</h2>
          </Reveal>
          <Reveal delay={0.14}>
            <div className="mt-10 flex flex-wrap gap-[10px]">
              {CAREERS.opportunities.map((area) => (
                <Chip key={area}>{area}</Chip>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* openings */}
      <section className="lk-section" id="openings" data-testid="careers-openings-section">
        <div className="lk-container-wide">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <Reveal>
                <SectionLabel>Current Openings</SectionLabel>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="display-lg mt-7">OPEN ROLES.</h2>
              </Reveal>
            </div>
            <Reveal delay={0.12}>
              <ArrowLink href={`mailto:${COMPANY.email}`} data-testid="careers-email-link">
                {COMPANY.email}
              </ArrowLink>
            </Reveal>
          </div>

          {loading && (
            <div className="mt-12 space-y-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-[92px] w-full animate-pulse" style={{ background: "var(--lykit-lavender)" }} />
              ))}
            </div>
          )}

          {!loading && openings.length === 0 && (
            <div className="mt-12 max-w-[52ch]" data-testid="careers-empty-state">
              <p className="lead">
                There are no published openings right now — but we always want to meet good people. Send us your
                profile and we will keep it on file.
              </p>
              <div className="mt-8">
                <PrimaryButton onClick={() => scrollToId("apply")}>Send Your Profile</PrimaryButton>
              </div>
            </div>
          )}

          {!loading && openings.length > 0 && (
            <>
              <ul className="mt-12" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {openings.map((opening, i) => {
                  const open = expanded === (opening.id || i);
                  return (
                    <li
                      key={opening.id || opening.title}
                      data-testid="career-opening"
                      style={{ borderTop: "1px solid rgba(23,21,34,0.12)" }}
                    >
                      <div className="flex flex-col gap-5 py-7 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <h3 className="font-display text-[clamp(17px,1.4vw,21px)] font-extrabold tracking-[-0.02em]">
                            {opening.title}
                          </h3>
                          <p
                            className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px]"
                            style={{ color: "rgba(23,21,34,0.58)" }}
                          >
                            <span>{opening.department}</span>
                            <span aria-hidden="true">&middot;</span>
                            <span>{opening.location}</span>
                            <span aria-hidden="true">&middot;</span>
                            <span>{opening.type}</span>
                            {opening.experience && (
                              <>
                                <span aria-hidden="true">&middot;</span>
                                <span>{opening.experience}</span>
                              </>
                            )}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => setExpanded(open ? null : opening.id || i)}
                            aria-expanded={open}
                            data-testid="career-opening-toggle"
                            className="text-[11.5px] font-semibold uppercase"
                            style={{ letterSpacing: "0.14em", color: "rgba(23,21,34,0.6)" }}
                          >
                            {open ? "Hide Details" : "View Details"}
                          </button>
                          <PrimaryButton onClick={() => applyTo(opening)} data-testid="career-apply-button">
                            Apply
                          </PrimaryButton>
                        </div>
                      </div>

                      {open && (
                        <div className="pb-9" data-testid="career-opening-details">
                          <p className="max-w-[64ch] text-[14px] leading-[1.66] body-muted">{opening.description}</p>
                          {opening.responsibilities?.length > 0 && (
                            <ul className="mt-6 space-y-3" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                              {opening.responsibilities.map((item) => (
                                <li key={item} className="flex gap-4 text-[14px]" style={{ color: "rgba(23,21,34,0.72)" }}>
                                  <span aria-hidden="true" style={{ color: "#B08A4B" }}>
                                    &mdash;
                                  </span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-10" style={{ borderTop: "1px solid rgba(23,21,34,0.12)", paddingTop: 24 }}>
                <PlaceholderNote>
                  Openings are placeholder records — live roles are published by the LYKIT team.
                </PlaceholderNote>
              </div>
            </>
          )}
        </div>
      </section>

      {/* apply */}
      <section className="cinematic" id="apply" data-testid="careers-apply-section">
        <div className="lk-container-wide py-[clamp(38px,4vw,66px)]">
          <div className="grid gap-y-12 lg:grid-cols-12 lg:gap-x-16">
            <div className="lg:col-span-5">
              <SectionLabel onDark>Send Your Profile</SectionLabel>
              <h2 className="display-lg mt-7 max-w-[16ch]" style={{ color: "#FBFAF7" }}>
                TELL US WHAT YOU CAN BUILD.
              </h2>
              <p className="mt-7 max-w-[44ch] text-[15px] leading-[1.68]" style={{ color: "rgba(247,246,242,0.74)" }}>
                Share your profile, portfolio or reel. If there is a fit — now or later — we will reach out.
              </p>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              {sent ? (
                <FormSuccess
                  testId="application-success"
                  title="PROFILE RECEIVED."
                  message="Thank you. Your profile has reached the LYKIT team and we will get in touch if there is a fit."
                >
                  <PrimaryButton variant="outline" onClick={() => setSent(false)} data-testid="application-reset">
                    Send Another Profile
                  </PrimaryButton>
                </FormSuccess>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid="career-application-form"
                  noValidate
                  className="rounded-[20px] p-6 sm:p-9"
                  style={{ background: "var(--lykit-ivory)" }}
                >
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Full Name" name="name" required value={form.name} onChange={onChange} error={errors.name} placeholder="Your name" />
                    <Field label="Email" name="email" type="email" required value={form.email} onChange={onChange} error={errors.email} placeholder="you@email.com" />
                    <Field label="Phone" name="phone" required value={form.phone} onChange={onChange} error={errors.phone} placeholder="+91" />
                    <SelectField
                      label="Area of Interest"
                      name="area"
                      required
                      value={form.area}
                      onChange={onChange}
                      options={CAREERS.opportunities}
                      error={errors.area}
                      placeholder="Select an area"
                    />
                    <div className="sm:col-span-2">
                      <Field
                        label="Applying For"
                        name="opening"
                        value={form.opening}
                        onChange={onChange}
                        placeholder="Role title (optional)"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Field
                        label="Portfolio / Reel / LinkedIn"
                        name="portfolio"
                        value={form.portfolio}
                        onChange={onChange}
                        placeholder="Link to your work"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <TextAreaField
                        label="About You"
                        name="message"
                        value={form.message}
                        onChange={onChange}
                        placeholder="A short note about your experience"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="career-submit-button"
                    className="btn btn-primary mt-9"
                    style={sending ? { opacity: 0.7, cursor: "wait" } : undefined}
                  >
                    {sending ? "Sending…" : "Send Your Profile"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <ClosingCTA />
    </>
  );
}
